import type {PhotoRequirement} from './types';

/**
 * While authoring, the controls a drawn element contains take no pointer events.
 *
 * The design canvas draws the real control now (see `PreviewElement.drawFilled`),
 * and a real control has a caret and a focus ring. The author is not filling
 * anything in: every press has to reach the wrapper above, which owns selection,
 * hover, drag and double-click-to-edit. Stated as a descendant selector rather
 * than a wrapper div, because an absolute element's height chains through its
 * wrapper and an extra one would break the geometry the author dragged.
 *
 * **One `:is()`, not a tag per variant.** It was four separate arbitrary
 * variants naming `input`, `textarea`, `select` and `button` — a list that says
 * nothing about what it is FOR, and so a list the next control added to a
 * renderer would quietly fall outside of. What belongs here is "anything the
 * browser will let a pointer act on", which is what this names: the four form
 * tags, plus a link and anything made editable in place.
 *
 * Applied per element and never to the three things that ARE authoring inside
 * one: a table (its cells hold themselves inert, and its own editing textarea
 * lives in this subtree), an inline label edit, and a crop.
 */
export const INERT_CONTROLS =
  '[&_:is(input,textarea,select,button,a,[contenteditable])]:pointer-events-none';

/**
 * `INERT_CONTROLS` for the elements it applies to, and '' for the rest.
 *
 * `fillMode` first, and it is the whole point: on a real report — or the
 * editor's own fill preview — the controls ARE the surface, and freezing them
 * would leave a crew unable to fill anything in.
 */
export function inertControls(
  item: PhotoRequirement,
  authoring: {fillMode: boolean; isInlineEditing: boolean; cropEditId: string | null},
): string {
  if (authoring.fillMode) return '';
  if (item.type === 'table') return '';
  if (authoring.isInlineEditing || authoring.cropEditId === item.id) return '';
  return INERT_CONTROLS;
}
